import {
  Badge,
  Button,
  Card,
  Input,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  mergeClasses,
  makeStyles,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import {
  ArrowLeft24Regular,
  ArrowRight24Regular,
  DocumentAdd24Regular,
  Edit24Regular,
  PersonAdd24Regular,
  PeopleTeam24Regular,
  Sparkle24Regular,
} from "@fluentui/react-icons";
import React, { useState } from "react";
import { useJob } from "../context/JobContext";
import { Page } from "../components/App";
import type { SelectedMember } from "../types";
import { taskPaneBackground } from "../theme";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
    boxSizing: "border-box",
    height: "88vh",
    ...taskPaneBackground,
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    overflow: "hidden",
    // Responsive spacing: on narrow panes we reduce padding so more content fits without feeling cramped.
    "@media (max-width: 420px)": {
      ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    },
    "@media (max-width: 520px)": {
      ...shorthands.gap(tokens.spacingVerticalS), // Reduce vertical whitespace so the main content stays legible.
    },
  },
  headerCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalXL),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalXXS), // Tighter title/subtitle spacing.
    flexShrink: 0,
    // Tighten header padding on smaller screens so the title doesn't eat the whole pane.
    "@media (max-width: 420px)": {
      ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    },
    "@media (max-width: 520px)": {
      // Very small panes: keep the header compact so the main card stays usable.
      ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
      ...shorthands.gap("0px"),
    },
  },
  headerTitle: {
    // IMPORTANT: because we render a real `h1` via `Text as="h1"`, the browser's default
    // stylesheet adds vertical margins unless we explicitly reset them.
    margin: 0,
    "@media (max-width: 520px)": {
      fontSize: tokens.fontSizeBase400,
      lineHeight: "1.1", // Tighter line-height keeps the header compact in tiny panes.
    },
  },
  headerSubtitle: {
    color: tokens.colorNeutralForeground3,
    // Same as above: `Text as="p"` renders a real paragraph with default margins unless reset.
    margin: 0,
    "@media (max-width: 520px)": {
      fontSize: tokens.fontSizeBase200,
      lineHeight: "1.1",
    },
  },
  headerEyebrow: {
    color: "#0f6cbd",
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: tokens.spacingVerticalXXS,
  },
  mainCard: {
    backgroundColor: "transparent",
    ...shorthands.border("0px", "solid", "transparent"),
    ...shorthands.padding(0, tokens.spacingHorizontalXXS, 0, 0),
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  overviewCard: {
    display: "flex",
    flexDirection: "row",
    flexShrink: 0,
    alignItems: "center",
    justifyContent: "space-between",
    ...shorthands.gap(tokens.spacingHorizontalS),
    color: "#123b63",
    backgroundColor: "#eff6ff",
    ...shorthands.border("1px", "solid", "#bfdbfe"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM),
    boxShadow: "none",
  },
  overviewIdentity: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    minWidth: 0,
  },
  overviewIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    flexShrink: 0,
    color: "#ffffff",
    backgroundColor: "#0f6cbd",
    ...shorthands.borderRadius("9px"),
  },
  overviewBadge: {
    flexShrink: 0,
  },
  leftColumn: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
    minHeight: 0,
    minWidth: 0,
  },
  leftScroll: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalM),
    "@media (max-width: 520px)": {
      ...shorthands.gap(tokens.spacingVerticalS),
    },
  },
  featureCard: {
    width: "100%",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", "#e2e8f0"),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    backgroundColor: "#f8fafc",
    boxShadow: "none",
    cursor: "pointer",
    transitionDuration: "120ms",
    transitionProperty: "border-color, background-color",
    "&:hover": {
      ...shorthands.borderColor("#93c5fd"),
      backgroundColor: "#f5f9ff",
    },
  },
  activeCard: {
    ...shorthands.borderColor("#60a5fa"),
    backgroundColor: "#eff6ff",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalM),
    marginBottom: tokens.spacingVerticalS, // Reduce header spacing so more of the card content stays visible.
  },
  cardTitleText: {
    display: "block",
    "@media (max-width: 520px)": {
      fontSize: tokens.fontSizeBase300,
      lineHeight: tokens.lineHeightBase300,
    },
  },
  cardBodyText: {
    display: "block",
    margin: 0,
    color: tokens.colorNeutralForeground3,
    "@media (max-width: 520px)": {
      fontSize: tokens.fontSizeBase200,
      lineHeight: tokens.lineHeightBase200,
    },
  },
  cardIcon: {
    width: "36px",
    height: "36px",
    flexShrink: 0,
    backgroundColor: "#eff6ff",
    ...shorthands.border("1px", "solid", "#dbeafe"),
    ...shorthands.borderRadius("9px"),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0f6cbd",
  },
  methodHeading: {
    margin: 0,
  },
  cardCopy: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("2px"),
    minWidth: 0,
  },
  customRuleSection: {
    backgroundColor: "#f8fafc",
    ...shorthands.border("1px", "solid", "#e2e8f0"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap(tokens.spacingVerticalXS),
  },
  rightPanel: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalM),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  },
  rightPanelTitle: {
    margin: 0,
    "@media (max-width: 520px)": {
      fontSize: tokens.fontSizeBase400,
      lineHeight: tokens.lineHeightBase400,
    },
  },
  rightPanelHeader: {
    display: "flex",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    marginBottom: tokens.spacingVerticalM,
  },
  membersList: {
    maxHeight: "180px",
    overflowY: "auto",
    ...shorthands.borderRadius("10px"),
    backgroundColor: "transparent",
    ...shorthands.gap(tokens.spacingVerticalXS),
    display: "flex",
    flexDirection: "column",
  },
  memberItem: {
    backgroundColor: "#f8fafc",
    ...shorthands.border("1px", "solid", "#e2e8f0"),
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  placeholder: {
    color: tokens.colorNeutralForeground4,
    textAlign: "center",
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalM),
    fontStyle: "italic",
  },
  rightPanelSummary: {
    marginTop: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground3,
  },
  footerCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", "#dbe4ef"),
    ...shorthands.borderRadius("14px"),
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalXL),
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    flexShrink: 0,
    display: "flex",
    flexWrap: "wrap", // Allow the footer to reflow on narrow panes rather than overlapping.
    justifyContent: "space-between",
    alignItems: "center",
    ...shorthands.gap(tokens.spacingHorizontalS),
    // Keep footer from stealing vertical space when labels wrap: scroll inside instead.
    maxHeight: "112px",
    overflowY: "auto",
    "@media (max-width: 520px)": {
      ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalL),
      alignItems: "stretch",
      maxHeight: "128px",
    },
  },
  footerActions: {
    display: "flex",
    ...shorthands.gap(tokens.spacingHorizontalS),
    flexWrap: "nowrap", // Keep Back/Continue on one line; labels shorten on narrow panes.
    width: "100%",
    justifyContent: "space-between",
    "@media (max-width: 520px)": {
      width: "100%",
      // Don't force stacking here; stacking increases footer height. Wrapping + footer scroll handles the tiny widths.
      flexDirection: "row",
    },
  },
  footerButton: {
    // Make both footer buttons visually consistent and more touch-friendly.
    minHeight: "44px",
    whiteSpace: "nowrap", // Prevent multi-line buttons; we switch to short labels instead.
    textAlign: "center",
    minWidth: 0, // Allows the button to shrink inside flex layouts without overflowing.
    "@media (max-width: 520px)": {
      fontSize: tokens.fontSizeBase200,
      lineHeight: tokens.lineHeightBase200,
    },
  },
  footerLabelLong: {
    display: "inline",
    "@media (max-width: 520px)": {
      display: "none",
    },
  },
  footerLabelShort: {
    display: "none",
    "@media (max-width: 520px)": {
      display: "inline",
    },
  },
});

export const MemberSelectionPage: React.FC<{ onNavigate: (page: Page) => void }> = ({
  onNavigate,
}) => {
  const classes = useStyles();
  const { state, dispatch } = useJob();
  const { method, selectedMembers, customRule, manualEntryType } = state.memberConfig;

  const [manualInput, setManualInput] = useState("");
  const [csvMessage, setCsvMessage] = useState<{ kind: "success" | "error"; text: string } | null>(
    null
  );
  const csvFileInputRef = React.useRef<HTMLInputElement | null>(null);

  const handleMethodSelect = (selectedMethod: "MANUAL" | "CSV" | "AI_DETECT") => {
    dispatch({ type: "SET_MEMBER_METHOD", payload: selectedMethod });
  };

  /**
   * Stable, case-insensitive key for de-duplication and list rendering.
   *
   * Priority:
   * - member_scheme_id (member-specific identifier)
   * - name
   */
  const getMemberKey = (m: SelectedMember): string => {
    const memberSchemeId = (m.member_scheme_id || "").trim().toLowerCase();
    if (memberSchemeId) {
      return `member_scheme_id:${memberSchemeId}`;
    }
    const name = (m.name || "").trim().toLowerCase();
    return `name:${name}`;
  };

  /**
   * Display label used in the Selected Members panel.
   *
   * We show both when available so users can sanity-check their input.
   */
  const formatMemberLabel = (m: SelectedMember): string => {
    const name = (m.name || "").trim();
    const memberSchemeId = (m.member_scheme_id || "").trim();
    if (name && memberSchemeId) {
      return `${name} (${memberSchemeId})`;
    }
    return name || memberSchemeId || "Unknown member";
  };

  /**
   * Merge new members into the existing list.
   *
   * - De-dupes by member_scheme_id if present, otherwise by name.
   * - If duplicated, enrich missing fields from the incoming member.
   */
  const mergeMembers = (
    incoming: SelectedMember[],
    existing: SelectedMember[]
  ): SelectedMember[] => {
    const byKey = new Map<string, SelectedMember>();

    // Keep current ordering (most-recent first) by seeding existing first.
    for (const m of existing) {
      const key = getMemberKey(m);
      if (key !== "name:" && key !== "member_scheme_id:") {
        byKey.set(key, { ...m });
      }
    }

    for (const m of incoming) {
      const key = getMemberKey(m);
      if (key === "name:" || key === "member_scheme_id:") {
        continue;
      }

      const prior = byKey.get(key);
      if (!prior) {
        // New member goes to the front (consistent with manual add behaviour).
        byKey.set(key, { ...m });
        continue;
      }

      // Enrich: keep existing values, fill blanks from incoming.
      byKey.set(key, {
        name: (prior.name || "").trim() ? prior.name : m.name,
        member_scheme_id: (prior.member_scheme_id || "").trim()
          ? prior.member_scheme_id
          : m.member_scheme_id,
      });
    }

    // Rebuild list with incoming members first (so the UI shows what they just added).
    const incomingKeys: string[] = [];
    for (const m of incoming) {
      const key = getMemberKey(m);
      if (key !== "name:" && key !== "member_scheme_id:" && byKey.has(key)) {
        incomingKeys.push(key);
      }
    }

    const seen = new Set<string>();
    const out: SelectedMember[] = [];
    for (const key of incomingKeys) {
      if (seen.has(key)) continue;
      seen.add(key);
      const m = byKey.get(key);
      if (m) out.push(m);
    }
    for (const [key, m] of byKey.entries()) {
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(m);
    }
    return out;
  };

  /**
   * Parse a delimited line (CSV-ish) with basic quoting support.
   *
   * This is intentionally small and dependency-free for the add-in. It supports:
   * - commas/semicolons/tabs as delimiters (chosen via detection on the header row)
   * - quoted fields with doubled quotes: "a ""quoted"" value"
   *
   * It does NOT attempt to handle multi-line quoted fields (rare for member lists).
   */
  const parseDelimitedLine = (line: string, delimiter: string): string[] => {
    const out: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const ch = line[i];

      if (ch === '"') {
        // Escaped quote inside a quoted field: "" -> "
        const next = line[i + 1];
        if (inQuotes && next === '"') {
          current += '"';
          i++;
          continue;
        }
        inQuotes = !inQuotes;
        continue;
      }

      if (!inQuotes && ch === delimiter) {
        out.push(current);
        current = "";
        continue;
      }

      current += ch;
    }

    out.push(current);
    return out;
  };

  /**
   * Detect a delimiter from the header row.
   *
   * We support comma, semicolon, and tab. (Some UK/EU CSV exports use semicolons.)
   */
  const detectDelimiter = (headerLine: string): string => {
    const candidates = [",", ";", "\t"];
    let best = ",";
    let bestCount = -1;

    for (const d of candidates) {
      const count = headerLine.split(d).length - 1;
      if (count > bestCount) {
        bestCount = count;
        best = d;
      }
    }

    return best;
  };

  /**
   * Convert a raw header string to a canonical key.
   *
   * Examples:
   * - "Member Scheme ID" -> "member_scheme_id"
   */
  const normaliseHeader = (h: string): string => {
    return h.trim().toLowerCase().replace(/\s+/g, "_").replace(/-+/g, "_");
  };

  const parseMembersCsv = (text: string): { members: SelectedMember[]; error?: string } => {
    // Remove UTF-8 BOM if present.
    const cleaned = text.replace(/^\uFEFF/, "");
    const lines = cleaned
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length === 0) {
      return { members: [], error: "CSV is empty." };
    }

    const delimiter = detectDelimiter(lines[0]);
    const rawHeaders = parseDelimitedLine(lines[0], delimiter).map((h) => h.trim());
    const headers = rawHeaders.map(normaliseHeader);

    // Strict CSV policy:
    // Clean-break policy: accept only the canonical selector headers.
    const allowedHeaders = new Set(["name", "member_scheme_id"]);
    const unsupportedHeaders = headers.filter((h) => !allowedHeaders.has(h));
    if (unsupportedHeaders.length > 0) {
      return {
        members: [],
        error: `Unsupported CSV header(s): ${unsupportedHeaders.join(", ")}. Allowed headers: name, member_scheme_id.`,
      };
    }

    const nameIdx = headers.findIndex((h) => h === "name");
    const memberIdIdx = headers.findIndex((h) => h === "member_scheme_id");

    if (nameIdx < 0 && memberIdIdx < 0) {
      return {
        members: [],
        error: "CSV header must include 'name' and/or 'member_scheme_id'.",
      };
    }

    const members: SelectedMember[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = parseDelimitedLine(lines[i], delimiter);
      const name = nameIdx >= 0 ? (cols[nameIdx] || "").trim() : "";
      const memberSchemeId = memberIdIdx >= 0 ? (cols[memberIdIdx] || "").trim() : "";

      if (!name && !memberSchemeId) {
        continue;
      }

      members.push({
        name: name || undefined,
        member_scheme_id: memberSchemeId || undefined,
      });
    }

    if (members.length === 0) {
      return {
        members: [],
        error: "No member rows found. Ensure at least one row has a name and/or member_scheme_id.",
      };
    }

    return { members };
  };

  const handleAddMember = () => {
    const raw = manualInput.trim();
    if (!raw) {
      return;
    }

    const newMember: SelectedMember =
      manualEntryType === "member_scheme_id" ? { member_scheme_id: raw } : { name: raw };
    const next = mergeMembers([newMember], selectedMembers);
    dispatch({ type: "SET_SELECTED_MEMBERS", payload: next });
    setManualInput("");
  };

  const handleRemoveMember = (member: SelectedMember) => {
    const removeKey = getMemberKey(member);
    dispatch({
      type: "SET_SELECTED_MEMBERS",
      payload: selectedMembers.filter((m) => getMemberKey(m) !== removeKey),
    });
  };

  const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const parsed = parseMembersCsv(text || "");
        if (parsed.error) {
          setCsvMessage({ kind: "error", text: parsed.error });
          return;
        }

        const next = mergeMembers(parsed.members, selectedMembers);
        dispatch({ type: "SET_SELECTED_MEMBERS", payload: next });
        setCsvMessage({
          kind: "success",
          text: `Added ${parsed.members.length} member${parsed.members.length === 1 ? "" : "s"} from CSV.`,
        });
      };
      reader.readAsText(file);
    }

    // Allow selecting the same file again to re-trigger onChange.
    event.target.value = "";
  };

  const canContinue = method === "AI_DETECT" || selectedMembers.length > 0;
  const methodLabel =
    method === "AI_DETECT" ? "AI detection" : method === "CSV" ? "CSV upload" : "Manual entry";

  return (
    <div className={classes.root}>
      {/* Hidden file input is always mounted so the button can trigger it reliably. */}
      <input
        ref={csvFileInputRef}
        type="file"
        id="csv-upload"
        accept=".csv,.txt"
        style={{ display: "none" }}
        onChange={handleCsvUpload}
      />

      <div className={classes.headerCard}>
        <div className={classes.headerEyebrow}>Member data extraction · Step 2</div>
        <Text as="h1" weight="bold" size={600} className={classes.headerTitle}>
          Choose members
        </Text>
        <Text as="p" size={300} className={classes.headerSubtitle}>
          Decide how the members included in this extraction should be identified.
        </Text>
      </div>

      <div className={classes.mainCard}>
        <Card className={classes.overviewCard}>
          <div className={classes.overviewIdentity}>
            <span className={classes.overviewIcon}>
              <PeopleTeam24Regular />
            </span>
            <div>
              <Text weight="semibold" block>
                Member scope
              </Text>
              <Text size={200}>
                {method === "AI_DETECT"
                  ? "All detected members"
                  : `${selectedMembers.length} selected`}
              </Text>
            </div>
          </div>
          <Badge appearance="filled" color="brand" className={classes.overviewBadge}>
            {methodLabel}
          </Badge>
        </Card>

        <div className={classes.leftColumn}>
          <div className={classes.leftScroll}>
            <Text as="h2" size={400} weight="semibold" className={classes.methodHeading}>
              Selection method
            </Text>
            <Card
              className={mergeClasses(
                classes.featureCard,
                method === "AI_DETECT" ? classes.activeCard : undefined
              )}
              onClick={() => handleMethodSelect("AI_DETECT")}
            >
              <div className={classes.cardHeader}>
                <div className={classes.cardIcon}>
                  <Sparkle24Regular />
                </div>
                <div className={classes.cardCopy}>
                  <Text weight="semibold" className={classes.cardTitleText}>
                    Let AI detect members
                  </Text>
                  <Text as="p" size={300} className={classes.cardBodyText}>
                    Automatically identify and extract data for all members.
                  </Text>
                </div>
              </div>
            </Card>

            <Card
              className={mergeClasses(
                classes.featureCard,
                method === "MANUAL" ? classes.activeCard : undefined
              )}
              onClick={() => handleMethodSelect("MANUAL")}
            >
              <div className={classes.cardHeader}>
                <div className={classes.cardIcon}>
                  <Edit24Regular />
                </div>
                <div className={classes.cardCopy}>
                  <Text weight="semibold" className={classes.cardTitleText}>
                    Enter members manually
                  </Text>
                  <Text as="p" size={300} className={classes.cardBodyText}>
                    Add individual member scheme IDs or names.
                  </Text>
                </div>
              </div>
              {method === "MANUAL" && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: tokens.spacingVerticalXS,
                  }}
                >
                  <RadioGroup
                    value={manualEntryType}
                    onChange={(_, data) =>
                      dispatch({
                        type: "SET_MEMBER_MANUAL_ENTRY_TYPE",
                        payload: data.value as "member_scheme_id" | "name",
                      })
                    }
                    layout="horizontal"
                  >
                    <Radio value="member_scheme_id" label="Member Scheme ID" />
                    <Radio value="name" label="Name" />
                  </RadioGroup>

                  <div style={{ display: "flex", gap: tokens.spacingHorizontalXS }}>
                    <Input
                      value={manualInput}
                      onChange={(e) => setManualInput(e.target.value)}
                      placeholder={
                        manualEntryType === "member_scheme_id"
                          ? "Enter member scheme ID..."
                          : "Enter member name..."
                      }
                      style={{ flex: 1 }}
                      onKeyDown={(e) => e.key === "Enter" && handleAddMember()}
                    />
                    <Button
                      onClick={handleAddMember}
                      disabled={!manualInput.trim()}
                      appearance="primary"
                    >
                      Add
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            <Card
              className={mergeClasses(
                classes.featureCard,
                method === "CSV" ? classes.activeCard : undefined
              )}
              onClick={() => handleMethodSelect("CSV")}
            >
              <div className={classes.cardHeader}>
                <div className={classes.cardIcon}>
                  <DocumentAdd24Regular />
                </div>
                <div className={classes.cardCopy}>
                  <Text weight="semibold" className={classes.cardTitleText}>
                    Upload a CSV list
                  </Text>
                  <Text as="p" size={300} className={classes.cardBodyText}>
                    Upload a CSV file with headers: name, member_scheme_id, or both.
                  </Text>
                </div>
              </div>
              {method === "CSV" && (
                <div>
                  <Button
                    icon={<PersonAdd24Regular />}
                    appearance="secondary"
                    onClick={(e) => {
                      // Prevent the card click from interfering with the file picker click.
                      e.stopPropagation();
                      csvFileInputRef.current?.click();
                    }}
                  >
                    Choose File
                  </Button>

                  {csvMessage && (
                    <Text
                      as="p"
                      size={200}
                      style={{
                        marginTop: tokens.spacingVerticalXS,
                        color:
                          csvMessage.kind === "error"
                            ? tokens.colorPaletteRedForeground1
                            : tokens.colorPaletteGreenForeground1,
                      }}
                    >
                      {csvMessage.text}
                    </Text>
                  )}
                </div>
              )}
            </Card>

            <div className={classes.customRuleSection}>
              <Text as="h3" weight="semibold" className={classes.methodHeading}>
                Member selection rule
              </Text>
              <Text as="p" size={300} className={classes.cardBodyText}>
                Provide specific instructions for member selection.
              </Text>
              <Textarea
                value={customRule || ""}
                onChange={(e) =>
                  dispatch({ type: "SET_MEMBER_CUSTOM_RULE", payload: e.target.value })
                }
                placeholder="e.g., 'Only extract data for retired members.'"
                style={{ width: "100%", minHeight: "96px" }}
              />
            </div>
          </div>
        </div>

        <div className={classes.rightPanel}>
          <div className={classes.rightPanelHeader}>
            <span className={classes.cardIcon}>
              <PeopleTeam24Regular />
            </span>
            <div>
              <Text as="h3" size={500} weight="semibold" className={classes.rightPanelTitle}>
                Selected members
              </Text>
              <Text size={200} className={classes.cardBodyText}>
                Review the member scope before continuing
              </Text>
            </div>
          </div>
          <div className={classes.membersList}>
            {method === "AI_DETECT" ? (
              <div className={classes.placeholder}>AI will automatically detect all members.</div>
            ) : selectedMembers.length > 0 ? (
              selectedMembers.map((member) => (
                <div key={getMemberKey(member)} className={classes.memberItem}>
                  <Text>{formatMemberLabel(member)}</Text>
                  <Button
                    onClick={() => handleRemoveMember(member)}
                    size="small"
                    appearance="transparent"
                  >
                    ✕
                  </Button>
                </div>
              ))
            ) : (
              <div className={classes.placeholder}>Choose a method to start adding members.</div>
            )}
          </div>
          <Text size={200} className={classes.rightPanelSummary}>
            {method === "AI_DETECT"
              ? "AI detection enabled"
              : `${selectedMembers.length} members selected`}
          </Text>
        </div>
      </div>

      <div className={classes.footerCard}>
        <div className={classes.footerActions}>
          <Button
            appearance="primary"
            className={mergeClasses(classes.footerButton, "sourcestone-navigation-primary")}
            icon={<ArrowLeft24Regular />}
            onClick={() => onNavigate("fileSelection")}
          >
            <span className={classes.footerLabelLong}>Back to File Selection</span>
            <span className={classes.footerLabelShort}>Back</span>
          </Button>
          <Button
            appearance="primary"
            className={mergeClasses(classes.footerButton, "sourcestone-navigation-primary")}
            icon={<ArrowRight24Regular />}
            iconPosition="after"
            onClick={() => onNavigate("fieldSelection")}
            disabled={!canContinue}
          >
            <span className={classes.footerLabelLong}>Continue to Field Selection</span>
            <span className={classes.footerLabelShort}>Continue</span>
          </Button>
        </div>
      </div>
    </div>
  );
};
